<template>
  <div
    ref="calendarContainerEl"
    :class="['calendar', { isMonthView: !isWeekView }, { isWeekView }]"
  >
    <CalendarHeader
      :embedded="embedded"
      :is-current-period="isCurrentPeriod"
      :left-panel-width="leftPanelWidth"
      :calendar-date="calendarDate"
      :is-three-day-view="isThreeDayView"
      :custom-days="customDays"
      :is-week-view="isWeekView"
      :is-weekend-shown="isWeekendShown"
      :is-left-pane-available="isLeftPaneAvailable"
      :show-date-navigation="showDateNavigation"
      :show-layout-selector="showLayoutSelector"
      :selectable-layouts="selectableLayouts"
      :layout-shortcuts="layoutShortcuts"
      :is-entitled-to-calendar-layouts="isEntitledToCalendarLayouts"
      :show-zoom-buttons="showZoomButtons"
      @next-period="nextPeriod"
      @prev-period="prevPeriod"
      @go-today="goToday"
      @go-to-date="(date) => $emit('updateCalendarDate', date)"
      @select-layout="onSelectLayout"
      @update-layout-shortcut="onUpdateLayoutShortcut"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
      :can-zoom-in="canZoomIn"
      :can-zoom-out="canZoomOut"
    />
    <ToggleAllDaySlot
      v-if="isWeekView && isAllDaySlotMinimizable"
      :is-all-day-slot-minimized="isAllDaySlotMinimized"
      @toggle-all-day-slot="toggleAllDaySlot"
    />
    <div v-show="isLoadingEvents" class="indeterminate-progress-bar">
      <div class="indeterminate-progress-bar__progress" />
    </div>
    <div
      :id="calendarDropZoneId"
      ref="calendarDropZoneEl"
      class="calendar-drop-zone"
    >
      <FullCalendar ref="calendarEl" :options="calendarOptions">
        <template #eventContent="{ event, isDragging, isResizing }">
          <div
            v-if="event.start && !isEventHidden(event)"
            class="event-content-wrapper"
          >
            <PlannerEventWrapper
              :id="event.id"
              :key="event.id"
              :is-all-day="event.allDay"
              :start="event.start"
              :end="event.end || undefined"
              :title="event.title"
              :extended-props="event.extendedProps"
              :class="readOnly ? undefined : draggableTaskClassName"
              :read-only="readOnly"
              :is-selected="openEventId === event.id"
              :is-right-click-allowed="
                allowContextMenu &&
                (!hasOpenEventChanges || openEventId !== event.id)
              "
              :is-moving="isResizing || isDragging"
              :selected-calendar-id="selectedCalendarId"
              :is-week-view="isWeekView"
              :is-left-pane-shown="isLeftPaneShown"
              :zoom-level="zoomLevel"
              :calendar-a-p-i="calendarAPI"
              @remove-event="removeEvent"
              @open="openEvent"
              @click="onEventClick(event.id)"
            />
            <span
              v-if="
                isDragging &&
                isDraggingAllocation &&
                isDuplicatingAllocation &&
                draggingAllocationEventId === event.id
              "
              class="duplicate-plus"
              >+</span
            >
          </div>
        </template>
      </FullCalendar>
    </div>
  </div>
</template>
<script lang="ts">
import type {
  CalendarApi,
  CalendarOptions,
  DateSelectArg,
  DayCellContentArg,
  DayCellMountArg,
  DayHeaderContentArg,
  DayHeaderMountArg,
  EventChangeArg,
  EventInput,
  EventMountArg,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, {
  EventDragStartArg,
  EventDragStopArg,
  EventReceiveArg,
  EventResizeStartArg,
  EventResizeStopArg,
} from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import FullCalendar from "@fullcalendar/vue3";
import { onKeyStroke } from "@vueuse/core";
import { addDays, addMonths, addWeeks, format, startOfDay } from "date-fns";
import { isEqual, isString } from "lodash-es";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";

import type {
  Daytime,
  EventCreation,
  EventId,
  TaskId,
} from "@ctrl/controller-rpc";

import { compareDaytime, getDaytime } from "@/components/dates";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import { iconToSvgString } from "@/components/icons/svg";
import * as inject from "@/components/inject";
import {
  buildMultipleEventsChoices,
  safeUpdateEvent,
} from "@/components/modal";
import { openDefaultCalendarModal } from "@/components/modals/default-calendar";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { plannerEventDetailsMainClassName } from "@/components/planner/sidebar/EventDetails.vue";
import ToggleAllDaySlot from "@/components/planner/ToggleAllDaySlot.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { defaultCalendarIssue } from "@/composables/useCalendars";
import { metrics } from "@/metrics";
import { useReleaser } from "@/ref";
import { allocateTimeForTask } from "@/task";
import { isGeneralizedTaskId } from "@/utils/id";
import { navigateToAgenda, navigateToObject } from "@/utils/navigate";
import { pause } from "@/utils/pause";
import { property } from "@/utils/unknown";

import { UseSettings } from "../settings/controller/settings";
import { getHTMLElement } from "../utils";
import CalendarHeader from "./CalendarHeader.vue";
import { draggableTaskClassName } from "./controllers/draggable";
import { handleMirrorEvent } from "./controllers/event";
import { patchSimultaneousEvents, useEvents } from "./controllers/events";
import {
  DEFAULT_SELECTABLE_LAYOUTS,
  formatCalendarDate,
  PlannerLayout,
  PlannerLayoutShortcuts,
  PlannerSelectableLayouts,
} from "./controllers/planner";
import {
  handleStaticGhostEvent,
  isStaticGhostEvent,
  useStaticGhostEvent,
} from "./controllers/static-ghost-event";
import { isAggregatedAllDayEvent } from "./helpers/aggregated-all-day-event";
import { buildEventTime } from "./helpers/build-event-time";
import { getMinimumEventEnd } from "./helpers/get-minimum-event-end";
import {
  getTimezoneCity,
  initialTimezones,
  slotLabelContent,
  visibleTimezones,
  withoutSeededTimezone,
  type TimezoneEntry,
} from "./helpers/timezones";
import PlannerEventWrapper from "./PlannerEventWrapper.vue";
import { calendarDropZoneId } from "./types";

const calendarEventOrderFields = (value: unknown) => {
  const extendedProps = property(value, "extendedProps");
  const event = property(extendedProps, "event");
  const overlappingIndex = property(extendedProps, "overlappingIndex");
  const title = property(value, "title");
  return {
    draft: !!property(event, "draft"),
    overlappingIndex:
      typeof overlappingIndex === "number" ? overlappingIndex : 0,
    allDay: property(value, "allDay") === true,
    title: typeof title === "string" ? title : "",
  };
};

export default defineComponent({
  components: {
    CalendarHeader,
    FullCalendar,
    PlannerEventWrapper,
    ToggleAllDaySlot,
  },
  // This component is a pure view over FullCalendar: every required prop
  // below is state owned by Planner.vue (the controller). Don't mount it
  // standalone — embed Planner instead, and see the TODO(planner) comment
  // there for how this split is meant to evolve.
  props: {
    calendarDate: { type: Date, required: true },
    dates: { type: Array as PropType<Date[]>, required: true },
    defaultCalendarId: { type: String },
    selectedCalendarId: { type: String },
    hasOpenEvent: { type: Boolean, required: true },
    hasOpenEventChanges: { type: Boolean, required: true },
    isCurrentPeriod: { type: Boolean, required: true },
    isDragCanceled: { type: Boolean, required: true },
    isDraggingResizing: { type: Boolean, required: true },
    isDraggingTask: { type: Boolean, required: true },
    isLeftPaneShown: { type: Boolean, required: true },
    isLeftPaneAvailable: { type: Boolean, default: true },
    // Display-only calendar: see Planner.vue's `readOnly` prop.
    readOnly: { type: Boolean, default: false },
    // Per-event right-click context menu: see Planner.vue's
    // `allowContextMenu` prop (the host resolves the `!readOnly` default).
    allowContextMenu: { type: Boolean, default: true },
    /** Inline header instead of the teleported one: see CalendarHeader. */
    embedded: { type: Boolean, default: false },
    /**
     * Render the multi-timezone axis (stored zones, labels, the "+" button).
     * When off the axis shows plain single-zone labels in the system
     * timezone, whatever the account has stored.
     */
    showTimezones: { type: Boolean, default: true },
    /** Header date display, picker and arrows: see CalendarHeader. */
    showDateNavigation: { type: Boolean, default: true },
    showLayoutSelector: { type: Boolean, default: true },
    selectableLayouts: {
      type: Object as PropType<PlannerSelectableLayouts>,
      default: () => ({ ...DEFAULT_SELECTABLE_LAYOUTS }),
    },
    layoutShortcuts: {
      type: Array as PropType<PlannerLayoutShortcuts>,
      required: true,
    },
    showZoomButtons: { type: Boolean, default: true },
    isThreeDayView: { type: Boolean, required: true },
    customDays: { type: Number, default: 3 },
    isWeekendShown: { type: Boolean, required: true },
    isWeekView: { type: Boolean, required: true },
    localStoragePrefix: { type: String, required: true },
    leftPanelWidth: { type: Number, default: 0 },
    openEventId: { type: String, required: false },
  },
  emits: {
    isDragCanceled: (_: boolean) => true,
    isDraggingResizing: (_: boolean) => true,
    isDraggingTask: (_: boolean, _id?: TaskId, _allocationEventId?: EventId) =>
      true,
    killEventDetails: () => true,
    selectEvent: (_id: string) => true,
    selectLayout: (_: PlannerLayout) => true,
    updateLayoutShortcut: (_index: number, _layout: PlannerLayout) => true,
    setupDropZone: (el: HTMLElement, elId: string) =>
      el instanceof HTMLElement && isString(elId),
    updateCalendarDate: (
      _date: Date,
      _options?: {
        avoidGoingToDate?: boolean;
        forceUpdate?: boolean;
      }
    ) => true,
  },
  async setup(props, { emit }) {
    const {
      calendarDate,
      dates,
      defaultCalendarId,
      selectedCalendarId,
      hasOpenEvent,
      hasOpenEventChanges,
      isCurrentPeriod,
      isDragCanceled,
      isDraggingResizing,
      isThreeDayView,
      customDays,
      isWeekendShown,
      isWeekView,
      openEventId,
    } = toRefs(props);
    const router = inject.router();
    const now = inject.clock();
    const theme = inject.theme();
    const direction = inject.direction();
    const controller = inject.controller();
    const calendars = inject.calendars();
    const logger = inject.logger("PlannerCalendar");
    const simpleModal = inject.simpleModal();
    const isMenuExpanded = inject.menu().isExpanded;
    const multipleEventsModal = inject.multipleEventsModal();
    const billing = inject.billing();
    const profile = inject.profile();
    const releaser = useReleaser();
    const calendarContainerEl = ref<Element>();
    const calendarEl = ref<InstanceType<typeof FullCalendar>>();
    const calendarDropZoneEl = ref<HTMLElement>();
    const { is24hFormat, isWeekStartOnSunday } = UseSettings.getInstance();
    const calendarAPI = () => calendarEl.value?.getApi?.();
    const isEntitledToTimeBlocking = computed(
      () => billing.value?.isEntitledTo("time-blocking")?.value ?? false
    );
    // Fail open while the entitlement is still loading (undefined).
    const isEntitledToCalendarLayouts = computed(
      () => billing.value?.isEntitledTo("calendar-layouts")?.value ?? true
    );
    const isTimeBlockingEntitled = (): boolean => {
      if (isEntitledToTimeBlocking.value) return true;
      openUpgradeModal(billing, undefined, "time-blocking");
      return false;
    };
    const onUpdateLayoutShortcut = (index: number, layout: PlannerLayout) =>
      emit("updateLayoutShortcut", index, layout);
    // Weekends are only ever hidden in week-style views (e.g. the 5-day view);
    // month always shows weekends, mirroring the `weekends` option above.
    watch([isWeekView, isWeekendShown], ([weekView, weekendShown]) => {
      calendarAPI()?.setOption("weekends", weekView ? weekendShown : true);
    });
    const { createStaticGhostEvent, removeStaticGhostEvent } =
      useStaticGhostEvent();
    const nextPeriod = (): void => {
      const target = getDateFromPeriod(1);
      emit("updateCalendarDate", target);
      updateCalendarSize();
    };
    const prevPeriod = (): void => {
      const target = getDateFromPeriod(-1);
      emit("updateCalendarDate", target);
      updateCalendarSize();
    };
    const getDateFromPeriod = (n: 1 | -1): Date =>
      isThreeDayView.value
        ? addDays(calendarDate.value, customDays.value * n)
        : isWeekView.value
          ? addWeeks(calendarDate.value, n)
          : addMonths(calendarDate.value, n);
    // EVENTS
    const {
      isAllDaySlotMinimizable,
      isAllDaySlotMinimized,
      isLoadingEvents,
      loadEvents,
      release: releaseEvents,
      timedEvents,
    } = useEvents(
      isWeekView,
      dates,
      defaultCalendarId.value,
      calendarAPI,
      controller,
      calendars,
      profile,
      {
        topPaneState: `${props.localStoragePrefix}:panes/top:state`,
        readOnly: props.readOnly,
      }
    );
    onMounted(() => loadEvents());
    releaser.defer(releaseEvents);
    // The reason is only logged (the parent's handler decides what to do):
    // diagnostic trail for events vanishing right after creation.
    const killEventDetails = (
      reason: string,
      args?: Record<string, unknown>
    ) => {
      logger.info("Requesting to kill open event details", {
        reason,
        hasOpenEvent: hasOpenEvent.value,
        ...args,
      });
      emit("killEventDetails");
    };

    // Track ALT for allocation duplication during calendar drag
    const altPressed = ref<boolean>(false);
    const isDraggingAllocation = ref<boolean>(false);
    const draggingAllocationEventId = ref<string | null>(null);
    const dragStopHandled = ref<boolean>(false);
    const isDuplicatingAllocation = computed(
      () => isDraggingAllocation.value && altPressed.value
    );
    const handleKeyDown = (e: KeyboardEvent) => {
      altPressed.value = !!e.altKey;
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      altPressed.value = !!e.altKey;
    };

    onMounted(() => {
      // First go to the correct date before rendering
      calendarAPI()?.gotoDate(calendarDate.value);
      calendarAPI()?.render();
      if (isWeekView.value) {
        if (isCurrentPeriod.value) scrollToCurrentTime();
        else scrollToStartTime();
      }
      pause(updateCalendarSize);
      pause(setDropZone);
      // Register keyboard listeners for ALT key detection during drag
      document.body.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
      document.body.addEventListener("keyup", handleKeyUp, {
        capture: true,
        passive: false,
      });
    });
    // Declared before the `onBeforeUnmount` registration below: the hook can
    // fire while the async setup is still suspended (Suspense discards the
    // pending branch), at which point consts declared after the first `await`
    // are still in their temporal dead zone (FRONTEND-FHN).
    const activeTippyInstances: TippyInstance[] = [];
    const destroyTippyInstances = () => {
      activeTippyInstances.forEach((t) => t.destroy());
      activeTippyInstances.length = 0;
    };
    onBeforeUnmount(() => {
      destroyTippyInstances();
      killEventDetails("calendar-unmounted");
      calendarAPI()?.destroy();
      calendarContainerEl.value = undefined;
      calendarEl.value = undefined;
      document.body.removeEventListener("keydown", handleKeyDown, true);
      document.body.removeEventListener("keyup", handleKeyUp, true);
    });

    const today = inject.today();
    const hoursConf = await releaser.add(controller.settings.hours());

    const toTimeString = (
      daytime?: Daytime | null,
      is24ForMidnightAllowed: boolean = false
    ): string => {
      const hours = daytime?.hours ?? 7;
      const minutes = daytime?.minutes ?? 0;
      const seconds = daytime?.seconds ?? 0;
      if (
        is24ForMidnightAllowed &&
        hours === 24 &&
        minutes === 0 &&
        seconds === 0
      ) {
        return "24:00:00";
      }
      return format(new Date(1970, 0, 1, hours, minutes, seconds), "HH:mm:ss");
    };

    // Helper functions to round boundaries to the nearest hour
    const roundDownToHour = (daytime: Daytime): Daytime => {
      return {
        hours: daytime.hours,
        minutes: 0,
        seconds: 0,
      };
    };

    const roundUpToHour = (daytime: Daytime): Daytime => {
      // If there are any minutes or seconds, round up to the next hour
      if ((daytime.minutes ?? 0) > 0 || (daytime.seconds ?? 0) > 0) {
        const nextHour = daytime.hours + 1;
        // Handle midnight (24:00:00)
        if (nextHour >= 24) {
          return { hours: 24, minutes: 0, seconds: 0 };
        }
        return { hours: nextHour, minutes: 0, seconds: 0 };
      }
      // Already on the hour, return as is
      return {
        hours: daytime.hours,
        minutes: 0,
        seconds: 0,
      };
    };

    // Compute dynamic boundaries for week mode from earliest/latest events vs
    // user-configured hours. Events are clipped to each displayed day: an
    // event spanning past midnight runs until 24:00 on one day and continues
    // from 00:00 on the next, so every visible day-segment contributes its
    // own boundaries (e.g. a 17:00 → 02:00 event widens the end to midnight
    // on its first day and the start to 00:00 on the next, when displayed).
    const visibleEventDaytimeRanges = computed<
      { start: Daytime; end: Daytime }[]
    >(() => {
      if (!isWeekView.value || !timedEvents.value) return [];
      const ranges: { start: Daytime; end: Daytime }[] = [];
      for (const e of timedEvents.value) {
        if (e.allDay || !(e.start instanceof Date) || !(e.end instanceof Date))
          continue;
        for (const day of dates.value) {
          const dayStart = startOfDay(day);
          const nextMidnight = addDays(dayStart, 1);
          // Skip days the event doesn't overlap (`end` is exclusive, so an
          // event ending exactly at midnight doesn't reach the next day).
          if (e.start >= nextMidnight || e.end <= dayStart) continue;
          ranges.push({
            start:
              e.start > dayStart
                ? getDaytime(e.start)
                : { hours: 0, minutes: 0, seconds: 0 },
            end:
              e.end < nextMidnight
                ? getDaytime(e.end)
                : { hours: 24, minutes: 0, seconds: 0 },
          });
        }
      }
      return ranges;
    });
    const weekMinDaytime = computed<Daytime>(() => {
      // Round down working hours boundary to the hour
      let earliest: Daytime = roundDownToHour(hoursConf.value.day);
      for (const { start } of visibleEventDaytimeRanges.value) {
        // Round down event time to the hour and compare
        const rounded = roundDownToHour(start);
        if (compareDaytime(rounded, earliest) < 0) {
          earliest = rounded;
        }
      }
      return earliest;
    });
    const weekMaxDaytime = computed<Daytime>(() => {
      const midnightAsZero = { hours: 0, minutes: 0, seconds: 0 };
      const midnightAsTwentyFour = { hours: 24, minutes: 0, seconds: 0 };
      let latest: Daytime =
        compareDaytime(hoursConf.value.evening, midnightAsZero) === 0
          ? midnightAsTwentyFour
          : roundUpToHour(hoursConf.value.evening);
      for (const { end } of visibleEventDaytimeRanges.value) {
        // Round up event time to the next hour and compare
        const rounded = roundUpToHour(end);
        if (compareDaytime(rounded, latest) > 0) {
          latest = rounded;
        }
      }
      return latest;
    });
    // Strings for FullCalendar options. `weekMinDaytime` / `weekMaxDaytime`
    // already resolve to the rounded working-hour boundaries in every timegrid
    // view (they only widen for events in week view). Both sides are padded by
    // one hour: the top label is hidden via CSS, the bottom is needed because
    // `slotMaxTime` is exclusive.
    const slotMinTimeString = computed<string>(() => {
      const min = weekMinDaytime.value;
      const padded: Daytime = {
        hours: Math.max(min.hours - 1, 0),
        minutes: 0,
        seconds: 0,
      };
      return toTimeString(padded);
    });
    const slotMaxTimeString = computed<string>(() => {
      const max = weekMaxDaytime.value;
      const padded: Daytime = {
        hours: Math.min(max.hours + 1, 24),
        minutes: 0,
        seconds: 0,
      };
      return toTimeString(padded, true);
    });
    // Zoom level: 0 = default, positive = zoom in (smaller slots), negative = zoom out (larger slots).
    // Namespaced per account (adopting the value written un-namespaced by
    // older versions on first read), like the timezones below.
    const {
      migrate,
      syncState: accountSyncState,
      unset: accountUnset,
    } = useBrowserStore(profile.value.user.accountId);
    migrate(`${props.localStoragePrefix}:zoom`);
    // Retire the dead "calendar:zoom" key: until the planner keys became
    // route-scoped, PlannerEvent wrote its zoom there, and it kept (re)seeding
    // that stale copy long after this calendar switched to the key above.
    // PlannerEvent now renders from this same route-scoped key.
    accountUnset("calendar:zoom");
    useBrowserStore().unset("calendar:zoom");
    const zoomLevel = releaser.add(
      accountSyncState<number>(`${props.localStoragePrefix}:zoom`, 0)
    );
    // Multi-timezone support
    //
    // FullCalendar only renders a single time axis column. We simulate
    // multiple timezones by keeping the calendar in "local" mode and
    // overriding three things:
    //  1. slotLabelContent – renders side-by-side times for each timezone
    //     inside a single <td>, using Intl.DateTimeFormat to shift the
    //     displayed hour.
    //  2. The axis <col>/<th> width – widened dynamically so the extra
    //     times fit without overlapping the event grid.
    //  3. The axis header – timezone labels are injected into the
    //     <th.fc-timegrid-axis> element via imperative DOM, each with a
    //     tippy menu for rename/delete.
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Persist only user-created display timezones. The system reference zone
    // is never stored: the calendar's own time column is that zone's spot,
    // materialized as a virtual entry on every mount (see visibleTimezones),
    // so it always follows the machine's timezone.
    const timezones = releaser.add(
      accountSyncState<TimezoneEntry[]>(
        `${props.localStoragePrefix}:timezones`,
        initialTimezones(localTz),
        { deep: true }
      )
    );
    // Drop the entry the pre-fix code seeded into storage, when provable
    // (see withoutSeededTimezone). Idempotent, so safe on every mount.
    const migratedTimezones = withoutSeededTimezone(timezones.value, localTz);
    if (migratedTimezones.length !== timezones.value.length) {
      timezones.value = migratedTimezones;
    }
    // The stored timezones drive the axis only when the host displays them
    // (see the `showTimezones` prop); the stored list itself stays untouched.
    const displayTimezones = computed<TimezoneEntry[]>(() =>
      props.showTimezones ? timezones.value : []
    );
    const ZOOM_LEVELS = [
      { slotDuration: "01:00:00", slotLabelInterval: "02:00:00" }, // Level -2: zoom out more
      { slotDuration: "00:30:00", slotLabelInterval: "01:00:00" }, // Level -1: zoom out
      { slotDuration: "00:15:00", slotLabelInterval: "01:00:00" }, // Level 0: default
      { slotDuration: "00:10:00", slotLabelInterval: "01:00:00" }, // Level 1: zoom in
      { slotDuration: "00:05:00", slotLabelInterval: "00:30:00" }, // Level 2: zoom in more
    ];
    const DEFAULT_ZOOM_LEVEL = 0;
    const DEFAULT_ZOOM_CONFIG = ZOOM_LEVELS[DEFAULT_ZOOM_LEVEL + 2]; // Level 0 maps to index 2
    const MIN_ZOOM_LEVEL = -2;
    const MAX_ZOOM_LEVEL = 2;
    const currentZoomConfig = computed(() => {
      const level = Math.max(
        MIN_ZOOM_LEVEL,
        Math.min(MAX_ZOOM_LEVEL, zoomLevel.value)
      );
      // Map level to index: -2 -> 0, -1 -> 1, 0 -> 2, 1 -> 3, 2 -> 4
      const index = level + 2;
      return ZOOM_LEVELS[index];
    });
    const slotDurationString = computed<string>(() =>
      isWeekView.value
        ? currentZoomConfig.value.slotDuration
        : DEFAULT_ZOOM_CONFIG.slotDuration
    );
    const slotLabelIntervalString = computed<string>(() =>
      isWeekView.value
        ? currentZoomConfig.value.slotLabelInterval
        : DEFAULT_ZOOM_CONFIG.slotLabelInterval
    );
    const canZoomIn = computed(() => zoomLevel.value < MAX_ZOOM_LEVEL);
    const canZoomOut = computed(() => zoomLevel.value > MIN_ZOOM_LEVEL);
    const zoomIn = () => {
      if (canZoomIn.value && isWeekView.value) {
        zoomLevel.value++;
        updateCalendarZoom();
      }
    };
    const zoomOut = () => {
      if (canZoomOut.value && isWeekView.value) {
        zoomLevel.value--;
        updateCalendarZoom();
      }
    };
    const updateCalendarZoom = () => {
      const api = calendarAPI();
      if (api && isWeekView.value) {
        // Preserve scroll position
        const scrollEl = api.el?.querySelector(".fc-scroller");
        let scrollTop = 0;
        if (scrollEl instanceof HTMLElement) {
          scrollTop = scrollEl.scrollTop;
        }

        // Update calendar options
        api.setOption("slotDuration", slotDurationString.value);
        api.setOption("slotLabelInterval", slotLabelIntervalString.value);

        // Restore scroll position after calendar re-renders
        nextTick(() => {
          pause(() => {
            const newScrollEl = api.el?.querySelector(".fc-scroller");
            if (newScrollEl instanceof HTMLElement && scrollTop > 0) {
              // Try to restore exact scroll position
              newScrollEl.scrollTop = scrollTop;
            }
          });
        });
      }
    };
    // Each rolling layout (1/2/3/4/6-day) is registered as its own FullCalendar
    // view, so changing the day count changes the view *type*, which forces a
    // rebuild with the new duration. Mutating a single view's duration in place
    // does not re-render the active view.
    const ROLLING_DAY_COUNTS = [1, 2, 3, 4, 6];
    const rollingViewName = (days: number) => `timeGridDays${days}`;
    const rollingViews: CalendarOptions["views"] = Object.fromEntries(
      ROLLING_DAY_COUNTS.map((days) => [
        rollingViewName(days),
        { type: "timeGrid", duration: { days }, buttonText: `${days} day` },
      ])
    );
    // FullCalendar passes a 1970-01-01 placeholder date to slotLabelContent
    // when timeZone is "local". Historical DST rules for 1970 differ from
    // today's, so formatting that date in another timezone yields wrong
    // offsets. We work around this by transferring the slot's hours/minutes
    // onto today's date before formatting.
    const refreshSlotLabels = () => {
      const api = calendarAPI();
      if (!api) return;
      api.setOption("slotLabelContent", (arg: { date: Date; text: string }) =>
        slotLabelContentFn(arg)
      );
    };
    // With no stored timezone the axis shows FullCalendar's native label
    // (arg.text) in the system timezone. With stored timezones we render
    // side-by-side spans inside a single <td> — the system zone keeps its
    // native spot beside the grid, styled as primary (bolder), with the
    // stored zones stacked before it.
    const slotLabelContentFn = (arg: { date: Date; text: string }) =>
      slotLabelContent(arg, displayTimezones.value, localTz, is24hFormat.value);
    const editSvg = iconToSvgString(EditIcon);
    const deleteSvg = iconToSvgString(DeleteIcon);
    const makeMenuBtn = (icon: string, label: string) => {
      const btn = document.createElement("button");
      btn.className = "fc-tz-menu-action";
      btn.innerHTML = `<span class="left"><span class="icon">${icon}</span><span class="text">${label}</span></span>`;
      return btn;
    };
    const createLabelMenu = (el: HTMLElement, index: number) => {
      const menuEl = document.createElement("div");
      menuEl.className = "fc-tz-menu-items";
      menuEl.tabIndex = -1;
      menuEl.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          inst.hide();
        }
      });
      const showActions = () => {
        menuEl.innerHTML = "";
        const renameBtn = makeMenuBtn(editSvg, "Rename");
        renameBtn.addEventListener("click", () => showRenameInput());
        menuEl.appendChild(renameBtn);
        const removeBtn = makeMenuBtn(deleteSvg, "Delete");
        removeBtn.classList.add("red");
        removeBtn.addEventListener("click", () => {
          inst.hide();
          timezones.value.splice(index, 1);
          nextTick(() => {
            refreshSlotLabels();
            injectTimezoneLabels();
            calendarAPI()?.updateSize();
          });
        });
        menuEl.appendChild(removeBtn);
      };
      const showRenameInput = () => {
        const currentLabel = timezones.value[index]?.label || "";
        menuEl.innerHTML = "";
        menuEl.className = "fc-tz-rename-wrap";
        const input = document.createElement("input");
        input.className = "fc-tz-menu-input";
        input.value = currentLabel;
        input.placeholder = "Label";
        input.addEventListener("keydown", (e) => {
          e.stopPropagation();
          if (e.key === "Enter" || e.key === "Escape") inst.hide();
        });
        input.addEventListener("input", () => {
          timezones.value[index].label = input.value;
          const labelSpan = el.querySelector(".fc-tz-label-text");
          if (labelSpan) labelSpan.textContent = input.value;
        });
        menuEl.appendChild(input);
        nextTick(() => input.focus());
      };
      showActions();
      const inst = tippy(el, {
        content: menuEl,
        interactive: true,
        trigger: "click",
        placement: "bottom-start",
        arrow: false,
        theme: `menu ${theme.value}`,
        duration: 0,
        offset: [0, 4],
        appendTo: () => document.querySelector("#theme") || document.body,
        onShow: () => {
          menuEl.className = "fc-tz-menu-items";
          showActions();
        },
        onShown: () => menuEl.focus(),
      });
      activeTippyInstances.push(inst);
    };
    type TzOption = {
      iana: string;
      city: string;
      offset: string;
      tzName: string;
      searchText: string;
      offsetMinutes: number;
    };
    let cachedTzOptions: TzOption[] | null = null;
    // IANA timezone identifiers use a single canonical city per zone (e.g.
    // America/Los_Angeles, Asia/Kolkata). These aliases let users search by
    // well-known city names that don't have their own IANA entry — e.g.
    // "San Francisco" → America/Los_Angeles, "Mumbai" → Asia/Kolkata.
    const CITY_ALIASES: ReadonlyArray<{ city: string; iana: string }> = [
      { city: "San Francisco", iana: "America/Los_Angeles" },
      { city: "Silicon Valley", iana: "America/Los_Angeles" },
      { city: "Seattle", iana: "America/Los_Angeles" },
      { city: "Las Vegas", iana: "America/Los_Angeles" },
      { city: "Portland", iana: "America/Los_Angeles" },
      { city: "Vancouver", iana: "America/Vancouver" },
      { city: "Boston", iana: "America/New_York" },
      { city: "Washington DC", iana: "America/New_York" },
      { city: "Miami", iana: "America/New_York" },
      { city: "Atlanta", iana: "America/New_York" },
      { city: "Philadelphia", iana: "America/New_York" },
      { city: "Dallas", iana: "America/Chicago" },
      { city: "Houston", iana: "America/Chicago" },
      { city: "Austin", iana: "America/Chicago" },
      { city: "Minneapolis", iana: "America/Chicago" },
      { city: "Munich", iana: "Europe/Berlin" },
      { city: "Frankfurt", iana: "Europe/Berlin" },
      { city: "Hamburg", iana: "Europe/Berlin" },
      { city: "Milan", iana: "Europe/Rome" },
      { city: "Barcelona", iana: "Europe/Madrid" },
      { city: "Lyon", iana: "Europe/Paris" },
      { city: "Marseille", iana: "Europe/Paris" },
      { city: "Nice", iana: "Europe/Paris" },
      { city: "Geneva", iana: "Europe/Zurich" },
      { city: "Edinburgh", iana: "Europe/London" },
      { city: "Manchester", iana: "Europe/London" },
      { city: "Mumbai", iana: "Asia/Kolkata" },
      { city: "Delhi", iana: "Asia/Kolkata" },
      { city: "Bangalore", iana: "Asia/Kolkata" },
      { city: "Chennai", iana: "Asia/Kolkata" },
      { city: "Beijing", iana: "Asia/Shanghai" },
      { city: "Shenzhen", iana: "Asia/Shanghai" },
      { city: "Guangzhou", iana: "Asia/Shanghai" },
      { city: "Osaka", iana: "Asia/Tokyo" },
      { city: "Kyoto", iana: "Asia/Tokyo" },
      { city: "Melbourne", iana: "Australia/Sydney" },
      { city: "Cape Town", iana: "Africa/Johannesburg" },
      { city: "Montreal", iana: "America/Toronto" },
      { city: "Abu Dhabi", iana: "Asia/Dubai" },
    ];
    const buildTzOptions = (): TzOption[] => {
      if (cachedTzOptions) return cachedTzOptions;
      const zones = Intl.supportedValuesOf("timeZone");
      const now = new Date();
      const aliasMap = new Map<string, string[]>();
      for (const a of CITY_ALIASES) {
        const list = aliasMap.get(a.iana) || [];
        list.push(a.city);
        aliasMap.set(a.iana, list);
      }
      const options: TzOption[] = zones.map((iana) => {
        const city = getTimezoneCity(iana);
        let offset = "";
        let tzName = "";
        let offsetMinutes = 0;
        try {
          const longParts = new Intl.DateTimeFormat("en", {
            timeZoneName: "longOffset",
            timeZone: iana,
          }).formatToParts(now);
          offset =
            longParts.find((p) => p.type === "timeZoneName")?.value || "";
          const nameParts = new Intl.DateTimeFormat("en", {
            timeZoneName: "long",
            timeZone: iana,
          }).formatToParts(now);
          tzName =
            nameParts.find((p) => p.type === "timeZoneName")?.value || "";
          const match = offset.match(/GMT([+-])(\d{2}):(\d{2})/);
          if (match) {
            const sign = match[1] === "+" ? 1 : -1;
            offsetMinutes =
              sign * (parseInt(match[2]) * 60 + parseInt(match[3]));
          }
        } catch {
          /* skip */
        }
        const getShort = (d: Date) => {
          try {
            return (
              new Intl.DateTimeFormat("en", {
                timeZoneName: "short",
                timeZone: iana,
              })
                .formatToParts(d)
                .find((p) => p.type === "timeZoneName")?.value || ""
            );
          } catch {
            return "";
          }
        };
        const jan = new Date(now.getFullYear(), 0, 15);
        const jul = new Date(now.getFullYear(), 6, 15);
        const shortStd = getShort(jan);
        const shortDst = getShort(jul);
        const aliases = aliasMap.get(iana)?.join(" ") || "";
        const searchText =
          `${iana} ${city} ${tzName} ${offset} ${shortStd} ${shortDst} ${aliases}`.toLowerCase();
        return { iana, city, offset, tzName, searchText, offsetMinutes };
      });
      options.sort((a, b) => a.offsetMinutes - b.offsetMinutes);
      cachedTzOptions = options;
      return options;
    };
    const createAddMenu = (btn: HTMLElement) => {
      const container = document.createElement("div");
      container.className = "fc-tz-search";
      const inputWrap = document.createElement("div");
      inputWrap.className = "fc-tz-search-input-wrap";
      const searchIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.2"/><path d="M11 11L14 14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;
      const iconSpan = document.createElement("span");
      iconSpan.className = "fc-tz-search-icon";
      iconSpan.innerHTML = searchIcon;
      inputWrap.appendChild(iconSpan);
      const input = document.createElement("input");
      input.className = "fc-tz-search-input";
      input.placeholder = "Search...";
      input.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Escape") inst.hide();
        else if (e.key === "ArrowDown") {
          e.preventDefault();
          const count = selectableRows().length;
          if (count) {
            selectedIdx = (selectedIdx + 1) % count;
            highlightSelected();
          }
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          const count = selectableRows().length;
          if (count) {
            selectedIdx = (selectedIdx - 1 + count) % count;
            highlightSelected();
          }
        } else if (e.key === "Enter") {
          e.preventDefault();
          confirmSelected();
        }
      });
      inputWrap.appendChild(input);
      container.appendChild(inputWrap);
      const listEl = document.createElement("div");
      listEl.className = "fc-tz-search-list";
      container.appendChild(listEl);
      // Zones already on the axis: the stored ones plus the system zone,
      // which is always displayed as the virtual reference column.
      const existingIanas = () =>
        new Set([localTz, ...timezones.value.map((t) => t.iana)]);
      let selectedIdx = 0;
      const selectableRows = (): HTMLElement[] =>
        Array.from(
          listEl.querySelectorAll<HTMLElement>(
            ".fc-tz-search-result:not(.disabled)"
          )
        );
      const highlightSelected = () => {
        listEl
          .querySelectorAll(".fc-tz-search-result.selected")
          .forEach((el) => el.classList.remove("selected"));
        const rows = selectableRows();
        if (rows.length && selectedIdx >= 0 && selectedIdx < rows.length) {
          rows[selectedIdx].classList.add("selected");
          rows[selectedIdx].scrollIntoView({ block: "nearest" });
        }
      };
      const confirmSelected = () => {
        const rows = selectableRows();
        if (rows.length && selectedIdx >= 0 && selectedIdx < rows.length) {
          rows[selectedIdx].click();
        }
      };
      const renderList = (query: string) => {
        listEl.innerHTML = "";
        const q = query.toLowerCase().trim();
        const all = buildTzOptions();
        const existing = existingIanas();
        const filtered = q ? all.filter((o) => o.searchText.includes(q)) : all;
        if (filtered.length === 0) {
          const empty = document.createElement("div");
          empty.className = "fc-tz-search-empty";
          empty.textContent = "No results";
          listEl.appendChild(empty);
          return;
        }
        const max = Math.min(filtered.length, 100);
        for (let i = 0; i < max; i++) {
          const opt = filtered[i];
          const row = document.createElement("div");
          row.className = "fc-tz-search-result";
          if (existing.has(opt.iana)) row.classList.add("disabled");
          row.innerHTML =
            `<span class="fc-tz-search-offset">${opt.offset}</span>` +
            `<span class="fc-tz-search-name">${opt.tzName}</span>` +
            `<span class="fc-tz-search-sep">–</span>` +
            `<span class="fc-tz-search-city">${opt.city}</span>`;
          if (!existing.has(opt.iana)) {
            row.addEventListener("click", () => {
              inst.hide();
              timezones.value.unshift({
                iana: opt.iana,
                label: opt.city,
              });
              nextTick(() => {
                refreshSlotLabels();
                injectTimezoneLabels();
                calendarAPI()?.updateSize();
              });
            });
          }
          listEl.appendChild(row);
        }
        selectedIdx = 0;
        highlightSelected();
      };
      input.addEventListener("input", () => renderList(input.value));
      const inst = tippy(btn, {
        content: container,
        interactive: true,
        trigger: "click",
        placement: "bottom-start",
        arrow: false,
        theme: `menu ${theme.value}`,
        duration: 0,
        offset: [0, 4],
        appendTo: () => document.querySelector("#theme") || document.body,
        onShow: () => {
          input.value = "";
          renderList("");
        },
        onShown: () => input.focus(),
      });
      activeTippyInstances.push(inst);
    };
    // FullCalendar sizes the axis column via a <col> element inside its
    // <colgroup>. We override its width directly so multiple timezone
    // labels and times have enough room. 62px per timezone keeps the
    // labels readable without eating too much of the event grid.
    const updateAxisColumnWidth = () => {
      const root = calendarAPI()?.el;
      if (!root) return;
      const cols = visibleTimezones(displayTimezones.value, localTz).length;
      const width = cols <= 1 ? "62px" : `${cols * 62}px`;
      root
        .querySelectorAll<HTMLElement>(
          "colgroup col:first-child, th.fc-timegrid-axis"
        )
        .forEach((el) => {
          el.style.width = width;
          el.style.minWidth = width;
        });
      // The planner footer's task columns must line up with the calendar's
      // day columns, so expose the rendered axis width (including borders)
      // as a CSS custom property on the shared `.days-wrapper` ancestor.
      const axis = root.querySelector("th.fc-timegrid-axis");
      const wrapper = root.closest<HTMLElement>(".days-wrapper");
      if (axis && wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const axisRect = axis.getBoundingClientRect();
        // Axis sits on inline-start; measure from that edge of the wrapper.
        const axisWidth =
          direction.value === "rtl"
            ? wrapperRect.right - axisRect.left
            : axisRect.right - wrapperRect.left;
        wrapper.style.setProperty("--planner-axis-width", `${axisWidth}px`);
      }
    };
    // Injects timezone label chips and the "+" button into the axis header
    // (<th.fc-timegrid-axis>). Built with imperative DOM because this area
    // lives inside FullCalendar's own markup, outside Vue's template.
    // Chips appear as soon as one user-created timezone exists: one chip per
    // visible time column, so even a single stored zone can be renamed or
    // removed. The system zone's native spot gets a plain, non-interactive
    // chip (there is nothing to rename or delete about it). With no stored
    // zones, the system time is displayed on the axis without any chip.
    const injectTimezoneLabels = () => {
      const root = calendarAPI()?.el;
      if (!root) return;
      updateAxisColumnWidth();
      const axisHeader = root.querySelector("th.fc-timegrid-axis");
      if (!axisHeader) return;
      destroyTippyInstances();
      axisHeader.querySelector(".fc-tz-labels")?.remove();
      // With timezones hidden the axis keeps FullCalendar's native labels:
      // no chips, no "+" button, and the width reset above already applied.
      if (!props.showTimezones) return;
      const div = document.createElement("div");
      div.className = "fc-tz-labels";
      // Read-only calendars keep the timezone labels (pure display) but not
      // the "+" button nor the label menus, which manage the timezone list.
      if (!props.readOnly) {
        const addBtn = document.createElement("button");
        addBtn.type = "button";
        addBtn.className = "fc-tz-add-btn";
        addBtn.textContent = "+";
        const addBtnTooltip = tippy(addBtn, {
          content: "Add time zone",
          placement: "top",
          arrow: false,
          animation: "fade",
          duration: 200,
          delay: [700, 0],
          theme: theme.value,
        });
        activeTippyInstances.push(addBtnTooltip);
        div.appendChild(addBtn);
        createAddMenu(addBtn);
      }
      const stored = timezones.value;
      if (stored.length) {
        // `visibleTimezones` keeps the stored order and at most appends the
        // virtual system entry, so index i < stored.length is a stored zone.
        visibleTimezones(stored, localTz).forEach((tz, i, visible) => {
          const interactive = !props.readOnly && i < stored.length;
          const label = document.createElement(interactive ? "button" : "span");
          if (label instanceof HTMLButtonElement) label.type = "button";
          const isPrimary = i === visible.length - 1;
          label.className = `fc-tz-label ${isPrimary ? "fc-tz-label-primary" : "fc-tz-label-secondary"}`;
          const text = document.createElement("span");
          text.className = "fc-tz-label-text";
          text.textContent = tz.label;
          label.appendChild(text);
          div.appendChild(label);
          if (interactive) {
            createLabelMenu(label, i);
          }
        });
      }
      axisHeader.appendChild(div);
    };
    const calendarOptions: CalendarOptions = {
      allDaySlot: true,
      dayHeaderFormat: { day: "2-digit", weekday: "short" },
      dayMaxEventRows: isWeekView.value ? false : true,
      defaultTimedEventDuration: "00:30:00",
      direction: direction.value,
      dragRevertDuration: 0,
      droppable: !props.readOnly,
      // Refuse the drop (no drop affordance, no allocation) for tasks that
      // cannot be allocated, i.e. bound tasks surfaced from a database row —
      // and for the whole month grid: the sidebar task sections are shown in
      // month view too, but a month cell has no time slot to allocate into.
      dropAccept: (draggedEl: HTMLElement) =>
        isWeekView.value && draggedEl.dataset.canAllocate !== "false",
      editable: isWeekView.value && !props.readOnly,
      eventOrder: isWeekView.value
        ? (a: unknown, b: unknown) => {
            const ea = calendarEventOrderFields(a);
            const eb = calendarEventOrderFields(b);
            // Push draft events to the bottom in all-day rows
            const aDraft = ea.draft;
            const bDraft = eb.draft;
            if (aDraft !== bDraft) return aDraft ? 1 : -1;
            // Preserve overlap ordering for stable stacking
            const aIdx = ea.overlappingIndex;
            const bIdx = eb.overlappingIndex;
            if (aIdx !== bIdx) return aIdx - bIdx;
            // Keep all-day before timed if needed, then title for determinism
            if (ea.allDay !== eb.allDay) return ea.allDay ? -1 : 1;
            return (ea.title || "").localeCompare(eb.title || "");
          }
        : "-allDay, start, title",
      eventTimeFormat: {
        hour: "numeric",
        minute: "2-digit",
        hour12: !is24hFormat.value,
      },
      firstDay: isWeekStartOnSunday.value ? 0 : 1,
      fixedWeekCount: true,
      headerToolbar: false,
      height: "100%",
      initialView: isThreeDayView.value
        ? rollingViewName(customDays.value)
        : isWeekView.value
          ? "timeGridWeek"
          : "dayGridMonth",
      navLinks: false,
      nowIndicator: true,
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      scrollTimeReset: false,
      selectable: !props.readOnly,
      selectMinDistance: 0,
      selectMirror: true,
      slotDuration: slotDurationString.value,
      slotLabelInterval: slotLabelIntervalString.value,
      slotLabelFormat: {
        hour: is24hFormat.value ? "2-digit" : "numeric",
        minute: is24hFormat.value ? "2-digit" : undefined,
        hour12: !is24hFormat.value,
      },
      slotLabelContent: slotLabelContentFn,
      // Limit visible hours in timeGrid views to hide night hours
      slotMinTime: slotMinTimeString.value,
      slotMaxTime: slotMaxTimeString.value,
      timeZone: "local",
      unselectCancel: `.${plannerEventDetailsMainClassName}`,
      views: rollingViews,
      weekends: isWeekView.value ? isWeekendShown.value : true,
      // METHODS
      datesSet: handleDatesSet,
      dayCellClassNames: handleDayCellClassNames,
      dayCellDidMount: handleDayCellDidMount,
      dayCellWillUnmount: handleDayCellWillUnmount,
      dayHeaderContent: handleDayHeaderContent,
      dayHeaderDidMount: handleDayHeaderDidMount,
      dayHeaderWillUnmount: handleDayHeaderWillUnmount,
      eventChange: handleEventChange,
      eventDidMount: handleEventDidMount,
      eventDragStart: handleEventDragStart,
      eventDragStop: handleEventDragStop,
      eventReceive: handleEventReceive,
      eventResizeStart: handleEventResizeStart,
      eventResizeStop: handleEventResizeStop,
      select: handleSelect,
    };

    /**
     * METHODS
     */
    function handleDatesSet() {
      nextTick(() => {
        injectTimezoneLabels();
      });
    }
    function handleDayCellClassNames({ date, view }: DayCellContentArg) {
      let classes = theme.value;
      if (!isWeekView.value) {
        const dateMonth = date.getMonth();
        const calendarDateMonth = view.calendar.getDate().getMonth();
        const isPast = dateMonth < calendarDateMonth;
        const isFuture = dateMonth > calendarDateMonth;
        if (isPast) classes += " isPast";
        else if (isFuture) classes += " isFuture";
      }
      return classes;
    }
    function handleDayCellDidMount({ el, date }: DayCellMountArg) {
      const dayEl = el.querySelector("a");
      if (!isWeekView.value && dayEl)
        dayEl.addEventListener("click", goToAgendaOnDate(date));
    }
    function handleDayCellWillUnmount({ el, date }: DayCellMountArg) {
      const dayEl = el.querySelector("a");
      if (!isWeekView.value && dayEl)
        dayEl.removeEventListener("click", goToAgendaOnDate(date));
    }
    function handleDayHeaderContent({
      text,
      isPast,
      isToday,
    }: DayHeaderContentArg) {
      const arr = text.split(" ");
      const number = arr[0];
      const letter = arr[1];
      if (isWeekView.value) {
        if (isToday)
          return {
            html: `<span class="isToday"><span class="darker">${letter}</span> <span class="highlight">${number}</span></span>`,
          };
        if (isPast)
          return { html: `<span class="past">${letter} ${number}</span>` };
        return { html: `${letter} <span class="darker">${number}</span>` };
      } else {
        return { html: `${letter}` };
      }
    }
    const goToAgendaOnDate = (date: Date) => () => {
      navigateToAgenda(router, date);
    };
    function handleDayHeaderDidMount({ el, date }: DayHeaderMountArg) {
      if (isWeekView.value && !props.readOnly)
        el
          .getElementsByTagName("a")
          .item(0)
          ?.addEventListener("click", goToAgendaOnDate(date));
    }
    function handleDayHeaderWillUnmount({ el, date }: DayHeaderMountArg) {
      if (isWeekView.value && !props.readOnly)
        el
          .getElementsByTagName("a")
          .item(0)
          ?.removeEventListener("click", goToAgendaOnDate(date));
    }
    function handleEventDidMount({ event, el, isMirror, view }: EventMountArg) {
      if (isMirror) {
        if (!event.id) {
          return handleMirrorEvent(
            event,
            hasOpenEventChanges.value,
            hasOpenEvent.value,
            view.calendar
          );
        }
      } else if (isStaticGhostEvent(event.id))
        return handleStaticGhostEvent(el);
    }
    function handleEventReceive({ event }: EventReceiveArg) {
      emit("isDraggingResizing", false);
      killEventDetails("task-dropped-on-calendar");
      // Required
      nextTick(() => {
        // Accept bound tasks (`object:…` ids) too: `allocateTimeForTask`
        // refuses them with an explanatory notification, instead of the
        // drop being silently discarded.
        if (
          !isDragCanceled.value &&
          event.start &&
          isGeneralizedTaskId(event.id)
        ) {
          if (!isTimeBlockingEntitled()) {
            emit("isDragCanceled", false);
            event.remove();
            return;
          }
          const date = event.start;
          void allocateTimeForTask(
            controller,
            profile.value.user.accountId,
            { id: event.id },
            {
              calendar: selectedCalendarId.value,
              duration: 1800,
              dates: { type: "date", date },
              daytime: [
                {
                  hours: date.getHours(),
                  minutes: date.getMinutes(),
                  seconds: date.getSeconds(),
                },
                null,
              ],
            },
            "app"
          );
        }
        emit("isDragCanceled", false);
        event.remove();
      });
    }
    async function handleSelect({
      view,
      start,
      end,
      allDay,
      jsEvent,
    }: DateSelectArg) {
      if (hasOpenEvent.value)
        return killEventDetails("new-selection-with-open-event");
      if (!isWeekView.value && jsEvent?.detail === 1)
        // Monthly view: do nothing on single click
        return;
      const calendarAPI = view.calendar;
      calendarAPI.unselect(); // clear date selection
      const minEnd = getMinimumEventEnd(start);
      const endTime = end > minEnd ? end : minEnd;
      // Draft Event
      if (defaultCalendarId.value) {
        const issue = defaultCalendarIssue(calendars.value?.default);
        if (issue) {
          // Don't create a draft that would be deleted right away (the grid
          // drops events of disabled calendars): explain and offer the fix.
          logger.warn("Cannot create draft event: default calendar issue", {
            issue,
            calendar: defaultCalendarId.value,
          });
          openDefaultCalendarModal(issue);
          return;
        }
        const event: EventCreation = {
          title: "",
          calendar: defaultCalendarId.value,
          draft: true,
          time: buildEventTime(allDay, start, endTime),
        };
        const id = await controller.event.create(event);
        logger.info("Created draft event from calendar selection", {
          eventId: id,
          calendar: defaultCalendarId.value,
        });
        emit("selectEvent", id);
      } else {
        logger.warn("Cannot create draft event: no default calendar");
        controller.notifications.notify(
          "It looks like Google calendar is not enabled on your Google suite account"
        );
      }
    }
    async function handleEventChange({ event: e, revert }: EventChangeArg) {
      if (isDragCanceled.value) {
        emit("isDragCanceled", false);
        return revert();
      }
      if (!e.id || !e.start) return;
      else if (isAggregatedAllDayEvent(e.id)) return revert();
      const event = await controller.event.get(e.id);
      event.release();
      if (
        !event.value ||
        // Avoid updating another event when having a draft
        (hasOpenEventChanges.value &&
          !event.value.draft &&
          openEventId.value !== event.value.id)
      ) {
        return revert();
      }
      const calendar = calendars.value?.byId.get(event.value.calendar);
      if (!calendar?.isWritable) return revert();
    }
    const onEventClick = (eventId: string) => {
      if (!eventId) return;
      else if (isAggregatedAllDayEvent(eventId)) return toggleAllDaySlot();
      else emit("selectEvent", eventId);
    };
    async function handleEventDragStart(args: EventDragStartArg) {
      const {
        event: { id },
        view,
        jsEvent,
      } = args;
      emit("isDraggingResizing", true);
      const cleanup = onKeyStroke(
        "Escape",
        (e) => {
          e.preventDefault();
          if (isDraggingResizing.value) {
            emit("isDragCanceled", true);
            const mouseUpEvent = new MouseEvent("mouseup", {
              bubbles: true,
              cancelable: true,
            });
            document.dispatchEvent(mouseUpEvent);
            view.calendar.trigger("eventDragStop", args);
          }
          cleanup(); // This will unregister the listener
        },
        { dedupe: true }
      );
      const event = await controller.event.get(id);
      if (event.value) createStaticGhostEvent(event.value, view);
      if (openEventId.value !== id) {
        killEventDetails("drag-start-of-other-event", { draggedEventId: id });
      }
      // Do NOT mark as task-dragging when dragging an existing event.
      //
      // Calendar switching (with TAB) for example, should only be enabled when
      // dragging a task from the sidebar i.e not an existing allocation.
      isDraggingAllocation.value = !!event.value?.task;
      draggingAllocationEventId.value = isDraggingAllocation.value ? id : null;
      dragStopHandled.value = false; // Reset flag for new drag
      // Check ALT key state from the drag event itself
      altPressed.value = jsEvent?.altKey || false;
      if (event.value?.task) {
        emit("isDraggingTask", true, event.value.task, id);
      }

      // Listen to mousemove during drag to check ALT key state
      // (keyboard events don't fire during drag, but mouse events do)
      const handleMouseMove = (e: MouseEvent) => {
        altPressed.value = !!e.altKey;
      };
      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove, true);
        document.removeEventListener("mouseup", handleMouseUp);
      };
      // Use capture phase to ensure we catch events even during drag
      document.addEventListener("mousemove", handleMouseMove, {
        passive: true,
        capture: true,
      });
      document.addEventListener("mouseup", handleMouseUp, { once: true });

      event.release();
    }
    const updateEvent = async (id: string, calendarAPI: CalendarApi) => {
      if (hasOpenEventChanges.value && openEventId.value !== id) return;
      const calendarEvent = calendarAPI.getEventById(id);
      const event = await controller.event.get(id);
      const eventValue = event.value;
      if (calendarEvent && eventValue) {
        const revert = () => {
          calendarEvent.setDates(eventValue?.start, eventValue?.end, {
            allDay: eventValue?.allDay,
          });
        };
        const { start, end, allDay } = calendarEvent;
        if (start) {
          const newTime = buildEventTime(allDay, start, end);
          if (!isEqual(newTime, eventValue.time)) {
            const patch = {
              id,
              time: newTime,
            };
            // For draft events, update directly without publishing
            if (eventValue.draft) {
              await controller.event.update(patch, false, "app");
            } else {
              safeUpdateEvent(
                "update",
                eventValue.id,
                {
                  calendarAPI,
                  controller,
                  simpleModal,
                  multipleEventsModal,
                },
                {
                  patch,
                  multipleEventsChoices: buildMultipleEventsChoices(
                    patch,
                    eventValue
                  ),
                  onCloseFunc: revert,
                }
              );
            }
          } else if (!eventValue.editable || !eventValue.movable) {
            controller.notifications.notify(
              `You don't have write permissions on this calendar: ${eventValue.calendar}.`
            );
          }
        }
      }
      event.release();
    };
    async function handleEventDragStop({
      event: { id },
      view,
      jsEvent,
    }: EventDragStopArg) {
      // Prevent duplicate execution
      if (dragStopHandled.value) return;
      dragStopHandled.value = true;

      emit("isDraggingResizing", false);
      removeStaticGhostEvent(view);
      // Update altPressed from the drop event to ensure computed isDuplicatingAllocation is accurate
      altPressed.value = !!jsEvent?.altKey;
      const event = await controller.event.get(id);
      const taskId = event.value?.task || undefined;
      // If drag was canceled, don't duplicate or move
      if (isDragCanceled.value) {
        // Reset flags
        altPressed.value = false;
        isDraggingAllocation.value = false;
        draggingAllocationEventId.value = null;
        event.release();
        emit("isDraggingTask", false, undefined);
        emit("isDragCanceled", false);
        return;
      }
      // If duplicating an allocation (Alt held), create a new allocation
      if (taskId && isDuplicatingAllocation.value) {
        if (!isTimeBlockingEntitled()) {
          const calendarEvent = view.calendar.getEventById(id);
          if (calendarEvent && event.value) {
            calendarEvent.setDates(event.value.start, event.value.end, {
              allDay: !!event.value.allDay,
            });
          }
          altPressed.value = false;
          isDraggingAllocation.value = false;
          draggingAllocationEventId.value = null;
          event.release();
          emit("isDraggingTask", false, undefined);
          return;
        }
        const calendarEvent = view.calendar.getEventById(id);
        const dropStart = calendarEvent?.start || null;
        const originalStart = event.value?.start || null;
        const originalEnd = event.value?.end || null;
        if (dropStart && originalStart && originalEnd) {
          const originalDurationMinutes =
            (originalEnd.getTime() - originalStart.getTime()) / (1000 * 60);
          // Duplicate allocation in the same calendar as original
          await allocateTimeForTask(
            controller,
            profile.value.user.accountId,
            { id: taskId },
            {
              calendar: event.value!.calendar,
              duration: Math.round(originalDurationMinutes * 60),
              dates: { type: "date", date: dropStart },
              daytime: [
                {
                  hours: dropStart.getHours(),
                  minutes: dropStart.getMinutes(),
                  seconds: dropStart.getSeconds(),
                },
                null,
              ],
            },
            "app"
          ).then(async (newEventId) => {
            if (newEventId && event.value?.attendance) {
              await controller.event.update(
                { id: newEventId, attendance: event.value.attendance },
                false,
                "app"
              );
            }
          });
          // Revert dragged event to its original time on the calendar
          calendarEvent?.setDates(originalStart, originalEnd, {
            allDay: !!event.value?.allDay,
          });
        }
        // Reset flags
        altPressed.value = false;
        isDraggingAllocation.value = false;
        draggingAllocationEventId.value = null;
        event.release();
        emit("isDraggingTask", false, undefined);
        if (jsEvent.target instanceof Document) emit("isDragCanceled", false);
        return; // Skip normal move update
      }
      // Reset flags if not duplicating
      altPressed.value = false;
      isDraggingAllocation.value = false;
      draggingAllocationEventId.value = null;
      event.release();
      const droppedTaskId = isDragCanceled.value ? undefined : taskId;
      emit("isDraggingTask", false, droppedTaskId, droppedTaskId && id);
      if (jsEvent.target instanceof Document) emit("isDragCanceled", false);
      else
        pause(() => {
          updateEvent(id, view.calendar);
        });
    }
    function handleEventResizeStart(args: EventResizeStartArg) {
      emit("isDraggingResizing", true);
      const { event, view } = args;
      if (openEventId.value !== event.id) {
        killEventDetails("resize-start-of-other-event", {
          resizedEventId: event.id,
        });
      }
      const cleanup = onKeyStroke(
        "Escape",
        (e) => {
          e.preventDefault();
          if (isDraggingResizing.value) {
            emit("isDragCanceled", true);
            const mouseUpEvent = new MouseEvent("mouseup", {
              bubbles: true,
              cancelable: true,
            });
            document.dispatchEvent(mouseUpEvent);
            view.calendar.trigger("eventResizeStop", args);
          }
          cleanup(); // This will unregister the listener
        },
        { dedupe: true }
      );
    }
    function handleEventResizeStop({
      event: { id },
      view,
    }: EventResizeStopArg) {
      emit("isDraggingResizing", false);
      pause(() => {
        updateEvent(id, view.calendar);
      });
    }
    const toggleAllDaySlot = () => {
      if (!isAllDaySlotMinimized.value)
        killEventDetails("all-day-slot-toggled");
      isAllDaySlotMinimized.value = !isAllDaySlotMinimized.value;
    };
    const openEvent = (
      v: { kind: "task" | "event"; id: TaskId | EventId },
      mouseEvent?: MouseEvent
    ) => navigateToObject(router, controller, v, mouseEvent);
    const scrollToCurrentTime = () => {
      // Scroll to half an hour before now so the now indicator never hugs
      // the top edge and some recent past stays visible.
      const minutes = Math.max(
        0,
        now.value.getHours() * 60 + now.value.getMinutes() - 30
      );
      const time = `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
      calendarAPI()?.scrollToTime(time);
    };
    const scrollToStartTime = () => {
      calendarAPI()?.scrollToTime(toTimeString(weekMinDaytime.value));
    };
    const updateCalendarSize = () => pause(() => calendarAPI()?.updateSize());
    releaser.defer(
      watch(
        isCurrentPeriod,
        (isCurrentPeriod) => {
          if (!isCurrentPeriod) return;
          // FIXME: HACK because open issue: https://github.com/fullcalendar/fullcalendar/issues/5639
          pause(scrollToCurrentTime);
        },
        { immediate: true }
      )
    );
    releaser.defer(
      watch(
        [isMenuExpanded, isLoadingEvents, isAllDaySlotMinimized],
        updateCalendarSize
      )
    );
    const setDropZone = () => {
      if (calendarDropZoneEl.value && isWeekView.value) {
        const el = getHTMLElement(calendarDropZoneEl.value);
        emit("setupDropZone", el, el.id);
      }
    };
    // React to hours config changes at runtime
    releaser.defer(
      watch([slotMinTimeString, slotMaxTimeString], ([min, max]) => {
        const api = calendarAPI();
        if (api) {
          api.setOption("slotMinTime", min);
          api.setOption("slotMaxTime", max);
        }
      })
    );
    releaser.defer(
      watch(direction, (dir) => {
        const api = calendarAPI();
        if (!api) return;
        api.setOption("direction", dir);
        nextTick(() => injectTimezoneLabels());
      })
    );
    // Switch FullCalendar view when the view *structure* changes
    // (month ↔ week ↔ rolling), including a change of rolling day-count. Using
    // `changeView` (rather than remounting) keeps the scroll position intact.
    releaser.defer(
      watch([isWeekView, isThreeDayView, customDays], ([week, threeDay]) => {
        const api = calendarAPI();
        if (!api) return;
        const view =
          !week && !threeDay
            ? "dayGridMonth"
            : threeDay
              ? rollingViewName(customDays.value)
              : "timeGridWeek";
        if (api.view.type !== view) api.changeView(view);
      })
    );
    // Reset zoom when switching views and update calendar when entering week view
    releaser.defer(
      watch(isWeekView, (newValue) => {
        if (!newValue) {
          // Reset zoom when leaving week view
          zoomLevel.value = 0;
        } else {
          // Update calendar options when entering week view (in case zoom level is non-zero)
          nextTick(() => {
            updateCalendarZoom();
          });
        }
      })
    );
    const removeEvent = (id: string) =>
      pause(() => {
        const api = calendarAPI();
        if (api) {
          const event = api.getEventById(id);
          if (event) {
            api.getEventById(id)?.remove();
            patchSimultaneousEvents(event, api.getEvents(), api, true);
          }
        }
        killEventDetails("event-removed-from-grid", { removedEventId: id });
      });
    const goToday = () => {
      const date = formatCalendarDate(today.value, isWeekStartOnSunday, {
        isThreeDayView,
        isWeekView,
      });
      calendarAPI()?.gotoDate(date);
      emit("updateCalendarDate", date, { avoidGoingToDate: true });
      updateCalendarSize();
    };
    const onSelectLayout = (layout: PlannerLayout) => {
      if (!isEntitledToCalendarLayouts.value) {
        openUpgradeModal(billing, undefined, "calendar-layouts");
        return;
      }
      metrics.segment.track("Calendar Layout Switched", {
        action: "switched",
        layout,
      });
      emit("selectLayout", layout);
    };
    const isEventHidden = (event: EventInput): boolean => {
      return !!(
        event.allDay &&
        !isAggregatedAllDayEvent(event.id) &&
        isAllDaySlotMinimized.value &&
        isAllDaySlotMinimizable.value
      );
    };

    return {
      calendarAPI,
      calendarContainerEl,
      calendarDropZoneId,
      calendarDropZoneEl,
      calendarEl,
      calendarOptions,
      canZoomIn,
      canZoomOut,
      draggableTaskClassName,
      draggingAllocationEventId,
      goToday,
      isAllDaySlotMinimizable,
      isAllDaySlotMinimized,
      isDraggingAllocation,
      isDuplicatingAllocation,
      isEntitledToCalendarLayouts,
      isEventHidden,
      isLoadingEvents,
      killEventDetails,
      loadEvents, // Exposed
      nextPeriod,
      onEventClick,
      onSelectLayout,
      onUpdateLayoutShortcut,
      openEvent,
      prevPeriod,
      removeEvent,
      toggleAllDaySlot,
      zoomIn,
      zoomLevel,
      zoomOut,
    };
  },
});
</script>
<style scoped lang="scss">
@use "@/styles/calendar.scss";

.event-content-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  // This wrapper is only for positioning the duplicate-plus indicator
  // It must take full width and height to not interfere with FullCalendar's sizing

  // Ensure the child component also takes full height
  :deep(> *) {
    height: 100%;
  }

  .duplicate-plus {
    position: absolute;
    inset-inline-end: 4px;
    top: 50%;
    transform: translateY(-50%);
    @extend %font-caption-regular;
    line-height: 1;
    opacity: 0.7;
    pointer-events: none;
    z-index: 10;
    font-weight: bold;
    @include themed() {
      color: t($secondary);
    }
  }
}

:deep(.planner-event) {
  position: relative;
}

// Hide FullCalendar's selection highlight completely
:deep(.fc-highlight) {
  background: transparent;
  border: none;
}
</style>
